import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container, Button, Icon, Header, Loader, Message, Segment, Table, Image } from 'semantic-ui-react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { useToast } from './utils/Toast';
import { supabase } from './supabaseClient';
import { isMockAuthEnabled, getMockSiteById } from './utils/mockAuth';
import { handleSessionExpired } from './utils/sessionHandler';
import { fetchAccessibleSiteById, fetchSiteById, SiteRecord, AuthTokenExpiredError, AccessDeniedError } from './api/sites';
import { generateSitePDF } from './utils/pdfGenerator';
import './SiteDetail.css';

type ViewState = {
  loading: boolean;
  error: string | null;
};

type Point = { x: number; y: number };

type HazardEntry = SiteRecord['hazardEntries'][number];

const getRectDimensions = (topLeft: Point, bottomRight: Point) => {
  const x = Math.min(topLeft.x, bottomRight.x);
  const y = Math.min(topLeft.y, bottomRight.y);
  const width = Math.abs(bottomRight.x - topLeft.x);
  const height = Math.abs(bottomRight.y - topLeft.y);
  return { x, y, width, height };
};

const formatReinspectionDate = (value?: string | null) => {
  if (!value || !value.trim()) {
    return 'None Required';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
};

const SiteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, refreshSession, role } = useAuth();
  const { showToast } = useToast();

  const [viewState, setViewState] = useState<ViewState>({ loading: true, error: null });
  const [site, setSite] = useState<SiteRecord | null>(null);
  const [activeHazardId, setActiveHazardId] = useState<number | null>(null);
  const [mapLoaded, setMapLoaded] = useState(true);
  const hazardTableRef = useRef<HTMLDivElement | null>(null);
  const [registerFlashActive, setRegisterFlashActive] = useState(false);
  const registerFlashTimerRef = useRef<number | null>(null);
  const rowScrollTimerRef = useRef<number | null>(null);

  const logoSrc = theme === 'dark'
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  const loadSite = useCallback(async () => {
    if (!id) {
      setSite(null);
      setViewState({ loading: false, error: 'Missing site identifier.' });
      return;
    }

    setViewState({ loading: true, error: null });
    try {
      // Return mock data if mock auth is enabled
      if (isMockAuthEnabled()) {
        const mockSite = getMockSiteById(id);
        if (mockSite) {
          setSite(mockSite as any);
          const firstHazard = (mockSite.drafts?.[0]?.hazards?.[0]?.id as any) ?? null;
          setActiveHazardId(firstHazard);
          setViewState({ loading: false, error: null });
        } else {
          setViewState({ loading: false, error: 'Site not found.' });
        }
        return;
      }
      
      const result = await (role === 'admin'
        ? fetchSiteById(id)
        : fetchAccessibleSiteById(id));
      setSite(result);
      const firstHazard = result.hazardEntries?.[0]?.id ?? null;
      setActiveHazardId(firstHazard);
      setViewState({ loading: false, error: null });
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }
      if (error instanceof AccessDeniedError) {
        setViewState({ loading: false, error: null });
        setMapLoaded(true);
        showToast('You do not have permission to view this site.', 'error');
        navigate('/no-access', { replace: true, state: { from: location.pathname } });
        return;
      }
      const message = error instanceof Error ? error.message : 'Unable to load this site.';
      setViewState({ loading: false, error: message });
      setMapLoaded(true);
      showToast(message, 'error');
    }
  }, [id, location.pathname, navigate, role, showToast]);

  useEffect(() => {
    void loadSite();
  }, [loadSite]);

  useEffect(() => () => {
    if (registerFlashTimerRef.current) {
      window.clearTimeout(registerFlashTimerRef.current);
      registerFlashTimerRef.current = null;
    }
    if (rowScrollTimerRef.current) {
      window.clearTimeout(rowScrollTimerRef.current);
      rowScrollTimerRef.current = null;
    }
  }, []);

  const scrollHazardRowIntoView = useCallback((hazardId: number, behavior: ScrollBehavior = 'smooth') => {
    const rowElement = document.querySelector<HTMLTableRowElement>(`[data-hazard-row='${hazardId}']`);
    if (!rowElement) {
      return;
    }

    const headerElement = document.querySelector<HTMLElement>('.site-detail-header');
    const headerOffset = headerElement ? headerElement.getBoundingClientRect().height : 0;
    const rowRect = rowElement.getBoundingClientRect();
    const absoluteTop = window.scrollY + rowRect.top;
    const targetTop = Math.max(absoluteTop - headerOffset - 24, 0);

    window.scrollTo({
      top: targetTop,
      behavior
    });
  }, []);

  const hazardLookup = useMemo(() => {
    if (!site?.hazardEntries) {
      return new Map<number, HazardEntry>();
    }
    return new Map(site.hazardEntries.map((entry) => [entry.id, entry] as const));
  }, [site]);

  const activeHazard = useMemo(() => {
    if (!site || activeHazardId === null) {
      return null;
    }
    return hazardLookup.get(activeHazardId) ?? null;
  }, [site, activeHazardId, hazardLookup]);

  const hazardCount = site?.hazardEntries?.length ?? 0;

  useEffect(() => {
    if (!site?.mapImageSrc) {
      setMapLoaded(true);
      return;
    }

    setMapLoaded(false);
    const image = new window.Image();
    image.src = site.mapImageSrc;

    const handleReady = () => {
      setMapLoaded(true);
    };

    image.addEventListener('load', handleReady);
    image.addEventListener('error', handleReady);

    return () => {
      image.removeEventListener('load', handleReady);
      image.removeEventListener('error', handleReady);
    };
  }, [site?.mapImageSrc]);

  const resolveHomeRoute = useCallback(() => {
    if (role === 'client') {
      return '/user-dashboard';
    }
    return '/dashboard';
  }, [role]);

  const handleBack = () => {
    navigate(resolveHomeRoute());
  };

  const handleEditSite = () => {
    if (!site) {
      return;
    }
    if (role !== 'admin') {
      return;
    }

    navigate('/sites/create', {
      state: { siteId: site.id }
    });
  };

  const handleGeneratePDF = async () => {
    if (!site) {
      return;
    }
    try {
      await generateSitePDF(site);
      showToast('PDF generated successfully!', 'success');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      showToast('Failed to generate PDF. Please try again.', 'error');
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      await refreshSession();
      showToast('Logged out successfully', 'success');
      navigate('/');
    } catch (err) {
      console.error('[SiteDetail] Failed to sign out', err);
      showToast('Unable to log out right now. Please try again.', 'error');
    }
  };

  const handleHazardSelection = (hazardId: number) => {
    setActiveHazardId(hazardId);
  };

  const handleScrollToRegister = () => {
    if (hazardTableRef.current) {
      hazardTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const targetHazardId = activeHazardId ?? site?.hazardEntries?.[0]?.id ?? null;
    if (targetHazardId !== null) {
      if (rowScrollTimerRef.current) {
        window.clearTimeout(rowScrollTimerRef.current);
      }
      rowScrollTimerRef.current = window.setTimeout(() => {
        scrollHazardRowIntoView(targetHazardId);
        rowScrollTimerRef.current = null;
      }, 350);
    }

    setRegisterFlashActive(true);
    if (registerFlashTimerRef.current) {
      window.clearTimeout(registerFlashTimerRef.current);
    }
    registerFlashTimerRef.current = window.setTimeout(() => {
      setRegisterFlashActive(false);
      registerFlashTimerRef.current = null;
    }, 1800);
  };

  const handleRefresh = () => {
    void loadSite();
  };

  return (
    <div className="site-detail-container">
      <div className="site-detail-header">
        <Container fluid>
          <div className="header-content">
            <div className="header-logo">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="dashboard-logo" onClick={handleBack} style={{ cursor: 'pointer' }} />
            </div>
            <div className="header-actions">
              <div className="header-user" title={user?.email ?? undefined}>
                <Icon name="user circle" />
                <span className="user-name">{user?.user_metadata?.full_name?.trim() || user?.email || 'Member'}</span>
              </div>
              <Button className="logout-button" onClick={handleLogout} icon labelPosition="right">
                <Icon name="sign out" />
                Logout
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <div style={{ padding: '0 1.5rem' }}><Container fluid className="site-detail-main">
        <div className="site-detail-topbar">
          <Button basic icon labelPosition="left" onClick={handleBack} className="back-button">
            <Icon name="arrow left" />
            Back to dashboard
          </Button>
          <div className="topbar-actions">
            <Button
              basic
              icon
              title="Refresh site"
              onClick={handleRefresh}
              disabled={viewState.loading}
              loading={viewState.loading}
              className="refresh-button"
            >
              <Icon name="redo" />
            </Button>
            {role === 'admin' ? (
              <Button primary icon labelPosition="left" onClick={handleEditSite} disabled={!site}>
                <Icon name="edit" />
                Edit site
              </Button>
            ) : (
              <Button primary icon labelPosition="left" onClick={handleGeneratePDF} disabled={!site}>
                <Icon name="file pdf outline" />
                Generate PDF
              </Button>
            )}
          </div>
        </div>

        {viewState.loading ? (
          <Segment className="site-detail-loading" basic>
            <Loader active inline="centered" content="Loading site details..." />
          </Segment>
        ) : viewState.error ? (
          <Message negative>
            <Message.Header>Unable to load site</Message.Header>
            <p>{viewState.error}</p>
            <Button basic color="red" onClick={handleRefresh}>
              <Icon name="refresh" /> Try again
            </Button>
          </Message>
        ) : !site ? (
          <Message info>
            <Message.Header>No site data available</Message.Header>
            <p>Select a different site from the dashboard.</p>
          </Message>
        ) : (
          <div className="site-detail-content">
            <header className="site-detail-summary">
              <div className="summary-heading">
                <h1>{site.siteName || 'Untitled site'}</h1>
                {site.location && (
                  <span className="summary-location">
                    <Icon name="map marker alternate" />
                    {site.location}
                  </span>
                )}
              </div>
              <div className="summary-meta">
                <div className="summary-meta-grid">
                  <div>
                    <span className="meta-label">Created</span>
                    <span className="meta-value">{site.createdAt ? new Date(site.createdAt).toLocaleString() : 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="meta-label">Last updated</span>
                    <span className="meta-value">{site.updatedAt ? new Date(site.updatedAt).toLocaleString() : 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="meta-label">Assigned users</span>
                    <span className="meta-value">{site.selectedUsers?.length ?? 0}</span>
                  </div>
                  <div>
                    <span className="meta-label">Hazard entries</span>
                    <span className="meta-value">{hazardCount}</span>
                  </div>
                </div>
              </div>
            </header>

            <div className="site-detail-grid">
              <section className="detail-panel">
                <div className="panel-header">
                  <Header as="h3">Site map</Header>
                </div>
                {site.mapImageSrc ? (
                  <div
                    className={`detail-map${site.mapOverlayActive ? ' has-overlay' : ''}`}
                    style={{ backgroundImage: `url(${site.mapImageSrc})` }}
                    aria-busy={!mapLoaded}
                  >
                    {!mapLoaded && (
                      <div className="detail-map-loader" role="status" aria-live="polite">
                        <Loader active inline="centered" inverted content="Loading map" />
                      </div>
                    )}
                    {site.mapRectangles?.map((rect) => {
                      const dims = getRectDimensions(rect.topLeft as Point, rect.bottomRight as Point);
                      return (
                        <div
                          key={`rect-${rect.id}`}
                          className="detail-map-rectangle"
                          style={{
                            left: `${dims.x}%`,
                            top: `${dims.y}%`,
                            width: `${dims.width}%`,
                            height: `${dims.height}%`
                          }}
                        />
                      );
                    })}

                    {site.mapPoints?.map((point) => {
                      const hazard = hazardLookup.get(point.hazardEntryId);
                      const isActivePoint = activeHazardId === point.hazardEntryId;
                      return (
                        <button
                          key={`point-${point.id}`}
                          type="button"
                          className={`detail-map-point${isActivePoint ? ' is-active' : ''}`}
                          style={{
                            left: `${point.position.x}%`,
                            top: `${point.position.y}%`
                          }}
                          onClick={() => handleHazardSelection(point.hazardEntryId)}
                          aria-label={hazard ? `Hazard item ${hazard.itemNumber}` : 'Hazard point'}
                          aria-pressed={isActivePoint}
                        >
                          {hazard ? hazard.itemNumber : '?'}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="detail-map-empty">
                    <Icon name="image outline" size="huge" />
                    <p>No map image uploaded for this site.</p>
                  </div>
                )}
              </section>

              <aside className="hazard-panel">
                {activeHazard ? (
                  <div className="hazard-content">
                    <Header as="h3">Hazard item {activeHazard.itemNumber}</Header>
                    <p className="hazard-location">
                      <Icon name="map marker alternate" />
                      {[activeHazard.areaLevel, activeHazard.roomLocation].filter(Boolean).join(' • ') || 'Location not specified'}
                    </p>
                    {activeHazard.photograph ? (
                      <Image
                        src={activeHazard.photograph}
                        rounded
                        fluid
                        className="hazard-photo"
                        alt={`Hazard ${activeHazard.itemNumber}`}
                      />
                    ) : (
                      <div className="hazard-photo placeholder">
                        <Icon name="camera" size="large" />
                        <span>No photograph uploaded</span>
                      </div>
                    )}
                    <div className="hazard-info-grid">
                      <div>
                        <span className="info-label">Hazard type</span>
                        <span className="info-value">{activeHazard.hazardType || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Status</span>
                        <span className="info-value">{activeHazard.hazardStatus || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Risk status</span>
                        <span className="info-value">{activeHazard.riskStatus || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Sample #</span>
                        <span className="info-value">{activeHazard.sampleNumber || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Friability</span>
                        <span className="info-value">{activeHazard.friability || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Disturbance potential</span>
                        <span className="info-value">{activeHazard.disturbancePotential || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Control priority</span>
                        <span className="info-value">{activeHazard.controlPriority || '—'}</span>
                      </div>
                      <div>
                        <span className="info-label">Reinspection</span>
                        <span className="info-value reinspection-text">{formatReinspectionDate(activeHazard.reInspectionDate)}</span>
                      </div>
                    </div>
                    <div className="hazard-notes">
                      <div>
                        <span className="info-label">Source of asbestos</span>
                        <p className="info-value">{activeHazard.sourceOfAsbestos || '—'}</p>
                      </div>
                      <div>
                        <span className="info-label">Comments</span>
                        <p className="info-value">{activeHazard.comments || 'No comments recorded.'}</p>
                      </div>
                      <div>
                        <span className="info-label">Recommendations</span>
                        <p className="info-value">{activeHazard.recommendations || 'No recommendations recorded.'}</p>
                      </div>
                    </div>
                    <Button secondary fluid icon labelPosition="right" onClick={handleScrollToRegister}>
                      Jump to register
                      <Icon name="arrow down" />
                    </Button>
                  </div>
                ) : (
                  <div className="hazard-placeholder">
                    <Icon name="info circle" size="large" />
                    <p>Select a hazard from the map or register.</p>
                  </div>
                )}
              </aside>
            </div>

            <section className="hazard-register" ref={hazardTableRef}>
              <div className="panel-header">
                <Header as="h3">Hazard register</Header>
                <span className="hazard-count">{hazardCount} entries</span>
              </div>
              <div className="register-table-wrapper">
                <Table celled structured selectable={false} compact="very" className="hazard-table" class="ui collapsing table">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell className='hazard-table-header-cell'>Item #</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Area/Level</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Room &amp; Location</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Feature</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Item Description</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Hazard Type</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Status</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Sample #</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Friability</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Source</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Disturb. Potential</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Condition</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Risk Status</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Approx. Qty</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Comments</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Control Priority</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Recommendations</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Reinspect</Table.HeaderCell>
                      <Table.HeaderCell className='hazard-table-header-cell'>Photograph</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {(site.hazardEntries ?? []).map((entry) => {
                      const isActiveRow = activeHazardId === entry.id;
                      const rowClassName = isActiveRow && registerFlashActive ? 'register-row-flash' : undefined;

                      return (
                        <Table.Row
                          key={entry.id}
                          onClick={() => handleHazardSelection(entry.id)}
                          className={rowClassName}
                          data-hazard-row={entry.id}
                        >
                          <Table.Cell>{entry.itemNumber}</Table.Cell>
                          <Table.Cell>{entry.areaLevel || '—'}</Table.Cell>
                          <Table.Cell>{entry.roomLocation || '—'}</Table.Cell>
                          <Table.Cell>{entry.buildingFeature || '—'}</Table.Cell>
                          <Table.Cell>{entry.materialItem || '—'}</Table.Cell>
                          <Table.Cell>{entry.hazardType || '—'}</Table.Cell>
                          <Table.Cell>{entry.hazardStatus || '—'}</Table.Cell>
                          <Table.Cell>{entry.sampleNumber || '—'}</Table.Cell>
                          <Table.Cell>{entry.friability || '—'}</Table.Cell>
                          <Table.Cell>{entry.sourceOfAsbestos || '—'}</Table.Cell>
                          <Table.Cell>{entry.disturbancePotential || '—'}</Table.Cell>
                          <Table.Cell>{entry.condition || '—'}</Table.Cell>
                          <Table.Cell>{entry.riskStatus || '—'}</Table.Cell>
                          <Table.Cell>{entry.approximateQuantity || '—'}</Table.Cell>
                          <Table.Cell>{entry.comments || '—'}</Table.Cell>
                          <Table.Cell>{entry.controlPriority || '—'}</Table.Cell>
                          <Table.Cell>{entry.recommendations || '—'}</Table.Cell>
                          <Table.Cell className="reinspection-cell">
                            <span className="reinspection-text">{formatReinspectionDate(entry.reInspectionDate)}</span>
                          </Table.Cell>
                          <Table.Cell>
                            {entry.photograph ? (
                              <Image
                                src={entry.photograph}
                                size="mini"
                                rounded
                                className="hazard-thumb"
                                alt={`Hazard ${entry.itemNumber}`}
                                onClick={(event: React.MouseEvent<HTMLImageElement>) => {
                                  event.stopPropagation();
                                  handleHazardSelection(entry.id);
                                }}
                              />
                            ) : (
                              <span className="no-photo">No photo</span>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </div>
            </section>
          </div>
        )}
      </Container></div>
    </div>
  );
};

export default SiteDetail;
